import React, { useState, useRef } from "react";
import api, { apiErr, receiptUrl, receiptPreviewUrl } from "@/lib/api";
import { DateConfirmBanner, dateFlagMessage, useDateConfirm } from "@/components/DateConfirmBanner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { Card, Btn, EmptyState, InfoTip } from "@/components/common";
import { fmtDate, taxYears, CURRENCY_FIELD_HELP, localISO, receiptBucket } from "@/lib/format";
import { MoneyInput } from "@/lib/accountingFormat";
import { curCode, readOutsideCurrency } from "@/lib/currencies";
import { toast } from "sonner";
import { Trash, ArrowRight, ArrowLeft, CheckCircle, Warning, CaretDown, Sparkle, Check, X, Clock, FilePdf, Image as ImageIcon, Plus } from "@phosphor-icons/react";
import { UnifiedDateInput } from "@/components/UnifiedDateInput";
import { CurrencySelect } from "@/components/CurrencySelect";
import { useParties } from "@/lib/parties";
import { usePaymentSources, psLabel, psMeta, psOwnerName, psForPayor } from "@/lib/payments";
import { PaidByPlanLine } from "@/components/PaidByPlanLine";
import { ReceiptViewer } from "@/components/ReceiptViewer";
import { readingFoundNothing, READING_FOUND_NOTHING, readAmount } from "@/lib/uploads";
import { categoryChoicesFor, reconcileCategory, CATEGORY_LOCKED_HINT, OTHER_MANUAL, isOffListCategory, offListCategoryHint, isReservedCategoryName } from "@/lib/categoryGate";
import { inSubmissionFlow, forTheSelectedBeneficiary, archivedBeneficiaryIds } from "@/lib/whoseWorkIsWaiting";
import { AddCategoryInline, ADD_CATEGORY_OPTION, ADD_CATEGORY_LABEL, NO_FAMILY_CATEGORIES_HINT } from "@/components/AddCategoryInline";
import { DRAW_QUESTION, DRAW_YES_LABEL, DRAW_NO_LABEL, DRAW_QUESTION_UNANSWERED } from "@/lib/aotcThirdAnswer";

// "＋ Add a category" opens the shared box in the panel itself — the one every
// screen uses — instead of sending the person to Settings in a second tab,
// where the half-done review was left behind and the new category was never
// chosen. (ZEN-512)

// The names this screen's one-off box refuses, for the same reason and in the
// same shape as the expense box: its own action entries, values AND the label a
// person can read off the screen. Nothing is added to what the picker OFFERS —
// what this screen DISPLAYS is untouched by ZEN-685. (Review rounds 2-5.)
const REVIEW_RESERVED_NAMES = [ADD_CATEGORY_OPTION, ADD_CATEGORY_LABEL, OTHER_MANUAL];

function ReceiptQueue({ students, selectedStudentId }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isOwner = user.role === "owner";
  const [active, setActive] = useState(null);
  const reviewPanelRef = React.useRef(null);

  const { data: receipts = [], isLoading } = useQuery({ queryKey: ["receipts"], queryFn: async () => (await api.get("/receipts")).data });

  const nameOf = (sid) => students.find((s) => s.id === sid)?.full_name || "Unassigned";
  const isAllView = !selectedStudentId;

  // WHOSE WORK IS ON THIS SCREEN — the same rule the server counts by, so the
  // number beside "Imports & Receipts" on the menu and the piles below it
  // cannot say different things. (ZEN-687; the rule itself, and why archived
  // beneficiaries are left out of the "All beneficiaries" view while a
  // beneficiary you deliberately pick still shows theirs, is in the module.)
  const archivedIds = React.useMemo(() => archivedBeneficiaryIds(students), [students]);
  const filterForStudent = (list) =>
    forTheSelectedBeneficiary(list, selectedStudentId, archivedIds);

  // THREE PILES, ONE RULE, AND THE RULE LIVES IN lib/format.js. This used to
  // ask "is it pending?" and file every other answer under Processed — so the
  // set-aside receipt ZEN-634 introduces would have been shown to a family as
  // finished, with a green tick, and nothing would have failed. A word the rule
  // does not recognise lands back in the queue, never in the finished pile.
  const inBucket = (b) => filterForStudent(receipts.filter((r) => receiptBucket(r.status) === b));

  // ★ THE TWO WAITING PILES ARE THE ACCOUNT OWNER'S OWN IMPORT WORK, AND
  // NOTHING ELSE. (ZEN-687.) A receipt a beneficiary sent for approval still
  // reads "pending" — sending it writes the other marker and leaves this one
  // alone — so it used to be listed HERE as well as on Approvals, with each
  // screen offering a different action on the same receipt. Rejecting it from
  // this screen is the door that records no reason and never tells the
  // beneficiary anything, which Maryse ruled on 2026-09-11 must not happen
  // (ZEN-608). Leaving those receipts to Approvals closes that door by
  // construction rather than by warning anybody about it.
  //
  // NOTHING DISAPPEARS FROM THE APP. Everything filtered out here is on the
  // Approvals screen: waiting on the account owner in its main list, and the
  // beneficiary's own not-yet-sent and returned ones under "Waiting on
  // student".
  //
  // THE FINISHED PILE IS LEFT ALONE — it is a read-only record of files
  // already dealt with, it is counted by nothing, and thinning it would lose
  // history for no gain.
  // Gift paperwork is not expense work: the server says which files those
  // are, by the same rule the badge counts with. (ZEN-757)
  const ownImportWork = (list) => list.filter((r) => !inSubmissionFlow(r) && !r.gift_paperwork);
  const pending = ownImportWork(inBucket("queue"));
  const setAside = ownImportWork(inBucket("set_aside"));
  const processed = inBucket("done");

  // WHERE "DECIDE LATER" GOES NEXT. Setting a receipt aside moves on to the
  // next one in the queue rather than closing the panel, so a person working
  // through a pile is not sent back to the pile after every one they park.
  // The next one is read from the list AS IT STANDS NOW, before the refetch
  // drops the parked receipt out of it — afterwards the index would point at
  // the receipt after the one we meant.
  const advancePast = (rec) => {
    const i = pending.findIndex((r) => r.id === rec.id);
    setActive(i >= 0 ? (pending[i + 1] || null) : null);
    qc.invalidateQueries({ queryKey: ["receipts"] });
    qc.invalidateQueries({ queryKey: ["attention"] });
  };

  // Auto-scroll to review panel when it opens
  React.useEffect(() => {
    if (active && reviewPanelRef.current) {
      reviewPanelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [active]);

  return (
    <>
      <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-[#6b7280]">Receipt queue ({pending.length})</h2>
          {isLoading ? <div className="h-24 animate-pulse rounded-xl bg-white" /> : pending.length === 0 ? (
            <EmptyState icon={Sparkle} title="Queue is clear" subtitle="Uploaded receipts awaiting review will appear here." />
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {pending.map((r) => (
                <QueueCard key={r.id} r={r} isAllView={isAllView} isOwner={isOwner} nameOf={nameOf} onReview={() => setActive(r)} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Review panel renders right after queue, BEFORE processed — and auto-scrolls into view */}
      {active && isOwner && (
        <div ref={reviewPanelRef}>
          {/* ★ KEYED ON THE RECEIPT, AND THIS IS NOT TIDINESS. Every box in the
              panel below is held in state that is filled in ONCE, from the
              receipt it opened with. Until "Decide later" there was no way to
              swap one receipt for another without closing the panel first, so
              nothing ever noticed. Moving straight to the next receipt does
              exactly that swap — and without this line the picture changes
              while the beneficiary, the date, the payee and THE AMOUNT stay on
              screen from the receipt just parked. Accepting then files one
              receipt's money against another's paperwork. Found by rendering
              it, not by reading it. */}
          <ReceiptReviewPanel key={active.id} receipt={active} students={students} selectedStudentId={selectedStudentId} onClose={() => setActive(null)}
            onDone={() => { setActive(null); qc.invalidateQueries({ queryKey: ["receipts"] }); qc.invalidateQueries({ queryKey: ["expenses"] }); qc.invalidateQueries({ queryKey: ["attention"] }); }}
            onSetAside={() => advancePast(active)} />
        </div>
      )}

      {/* A RECEIPT SET ASIDE IS NOT A RECEIPT DEALT WITH, so it gets its own
          place rather than a chip in the finished pile. It is a full card with
          the same Review button as the queue, because the point of the button
          that put it here is that you can come back to it. The heading repeats
          the words on the button that sends a receipt here, so nobody has to
          work out that "Decide later" and this pile are the same thing. */}
      {setAside.length > 0 && (
        <div className="mt-6" data-testid="set-aside-section">
          <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-[#6b7280]">Decide later ({setAside.length})</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {setAside.map((r) => (
              <QueueCard key={r.id} r={r} isAllView={isAllView} isOwner={isOwner} nameOf={nameOf} onReview={() => setActive(r)} testidPrefix="set-aside-receipt" />
            ))}
          </div>
        </div>
      )}

      {processed.length > 0 && (
        <>
          <h2 className="mb-3 mt-6 text-sm font-bold uppercase tracking-wide text-[#6b7280]">Processed ({processed.length})</h2>
          <div className="flex flex-wrap gap-2">
            {processed.map((r) => (
              <span key={r.id} className="inline-flex items-center gap-1.5 rounded-full border border-[#C2C9D1] bg-white px-3 py-1 text-xs font-semibold" data-testid={`processed-receipt-${r.id}`}>
                {r.status === "rejected" ? <X size={12} className="text-[#F01919]" /> : <Check size={12} className="text-[#077A39]" />}
                {r.original_filename}
              </span>
            ))}
          </div>
        </>
      )}
    </>
  );
}



// ONE CARD, TWO PILES. The queue and the set-aside pile show the same receipt
// the same way — filename, whose it is, what the reader made of it, and the way
// back in. Written once so the second pile cannot quietly lose the beneficiary
// label or the "AI read failed" warning the first one shows.
function QueueCard({ r, isAllView, isOwner, nameOf, onReview, testidPrefix = "receipt-card" }) {
  return (
    <Card className="flex flex-col gap-2" data-testid={`${testidPrefix}-${r.id}`}>
      <div className="flex items-center gap-2 text-sm">
        {r.content_type?.includes("pdf") ? <FilePdf size={20} className="text-[#F01919]" /> : <ImageIcon size={20} className="text-[#034AA2]" />}
        <span className="truncate font-semibold">{r.original_filename}</span>
      </div>
      {isAllView && (
        <div className="text-[10px] font-semibold uppercase tracking-wide text-[#6b7280]" data-testid={`receipt-student-label-${r.id}`}>
          {nameOf(r.student_id)}
        </div>
      )}
      {r.draft && (
        <div className="rounded-md bg-[#E9E9EB] p-2 text-xs">
          <div><b>{r.draft.payee || "Unknown payee"}</b></div>
          <div className="text-[#6b7280]">{r.draft.transaction_date ? fmtDate(r.draft.transaction_date) : "?"} · {r.draft.category || "?"} · {readAmount(r.draft) ?? "?"} {r.draft.original_currency || ""}</div>
        </div>
      )}
      {r.ai_error && <div className="text-xs text-[#F01919]">AI read failed — review manually</div>}
      {readingFoundNothing(r) && <div className="text-xs text-[#92600A]" data-testid={`reading-found-nothing-${r.id}`}>{READING_FOUND_NOTHING}</div>}
      {isOwner ? (
        <Btn onClick={onReview} data-testid={`review-receipt-${r.id}`}><Sparkle size={14} weight="fill" /> Review & accept</Btn>
      ) : <div className="text-xs text-[#6b7280]">Waiting for owner to accept</div>}
    </Card>
  );
}


function ReceiptReviewPanel({ receipt, students, selectedStudentId, onClose, onDone, onSetAside }) {
  const d = receipt.draft || {};
  // Receipt's own student_id is the source of truth; fall back to the view's selected student
  const initStudentId = receipt.student_id || selectedStudentId || "";
  // Lock the field ONLY when it equals the receipt's stored assignment
  const isStudentLocked = !!receipt.student_id && initStudentId === receipt.student_id;
  const receiptAssignedName = receipt.student_id
    ? students.find((s) => s.id === receipt.student_id)?.full_name || "another beneficiary"
    : null;
  const selectedStudent = students.find((s) => s.id === initStudentId);
  const { data: parties } = useParties();
  const qc = useQueryClient();

  const [f, setF] = useState({
    student_id: initStudentId,
    transaction_date: d.transaction_date || localISO(),
    payee: d.payee || "", description: d.description || "",
    // NOT PRE-FILLED, AND NO LONGER DEFAULTED TO THE FIRST CATEGORY ON THE LIST.
    // This used to open on cats[0] — "Tuition & Fees" — whenever the receipt
    // reading suggested nothing, so a category nobody had chosen sat on the
    // screen above the question that decides which list it may come from. The
    // reading's own suggestion is parked and re-offered once that question is
    // answered. (ZEN-282)
    category: "", _suggested_category: d.category || "",
    // What the scanner read, or US dollars when it read none; a code the app
    // does not know is left for the family to choose, never guessed. (ZEN-830)
    original_currency: readOutsideCurrency(d.original_currency).code, original_amount: readAmount(d) ?? "",
    // WHEN THE READER FOUND NO ANSWER, THERE IS NO ANSWER. This used to collapse
    // "the receipt did not say" onto false and send it with every accept, so an
    // expense the owner never classified was filed as "not 529-eligible". null
    // means unanswered, and it is omitted from the request. (ZEN-114)
    eligible_529: typeof d.eligible_529 === "boolean" ? d.eligible_529 : null,
    draw_from_529: typeof d.eligible_529 === "boolean" ? d.eligible_529 : null,
    reimbursed_by_ref: "none",
    payment_source_id: null, notes: "",
    payor_type: "", payor_party_id: "",
  });
  const [receiptFilename, setReceiptFilename] = useState(receipt.original_filename || "");
  const [customCatOpen, setCustomCatOpen] = useState(false);
  const [customCatName, setCustomCatName] = useState("");
  const [addCatOpen, setAddCatOpen] = useState(false);
  // The form as it is NOW, for a save that answers after the person moved on.
  const formNow = useRef(f);
  formNow.current = f;
  const [addReimburserOpen, setAddReimburserOpen] = useState(false);
  const [newReimburserNick, setNewReimburserNick] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const { data: psData } = usePaymentSources(false);
  const psSources = psData?.sources || [];
  // WHO PAID DECIDES THE MENU — the one shared rule, not a copy. An account
  // already picked that does not fit the payer now chosen is dropped, never
  // saved alongside them. (ZEN-764)
  const payerAccounts = psForPayor(psSources, f.payor_type, f.student_id, null);
  React.useEffect(() => {
    if (!f.payment_source_id) return;
    if (!payerAccounts.some((s) => s.id === f.payment_source_id)) set("payment_source_id", null);
  }, [f.payor_type, f.student_id, f.payment_source_id, psSources]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Layer 2: fuzzy duplicate check (payee + amount + date)
  const [dupWarnings, setDupWarnings] = useState([]);
  React.useEffect(() => {
    const check = async () => {
      if (!f.student_id || !f.payee || !f.original_amount || !f.transaction_date) { setDupWarnings([]); return; }
      try {
        const { data } = await api.get("/receipts/check-duplicate", { params: { student_id: f.student_id, payee: f.payee, amount: f.original_amount, date: f.transaction_date } });
        setDupWarnings(data.duplicates || []);
      } catch { setDupWarnings([]); }
    };
    const t = setTimeout(check, 500);
    return () => clearTimeout(t);
  }, [f.student_id, f.payee, f.original_amount, f.transaction_date]);

  // The category list follows whichever beneficiary is selected right now.
  const activeStu = students.find((s) => s.id === f.student_id);
  const activeStuSchools = (activeStu?.schools || []).filter(s => !s.archived);
  const activeGroup = activeStuSchools[0]?.expense_group || "post_secondary";
  const familyCategories = activeStu?.noneligible_categories || [];
  const choices = categoryChoicesFor({ eligible: f.eligible_529, expenseGroup: activeGroup, familyCategories });

  // Answering the 529 question decides which list the category comes from, so
  // it also decides what happens to one already on the form. Kept if the newly
  // chosen list still contains it, otherwise back to the placeholder. The
  // reading's suggestion is re-offered here rather than shown up front. (ZEN-282)
  const answerEligibility = (next) => {
    const candidate = f.category || f._suggested_category || "";
    setF((p) => ({
      ...p,
      eligible_529: next,
      draw_from_529: next === false ? false : p.draw_from_529,
      category: reconcileCategory({ category: candidate, eligible: next, expenseGroup: activeGroup, familyCategories }),
    }));
  };

  // AND THE SAME RECONCILIATION WHEN THE BENEFICIARY CHANGES. The beneficiary
  // decides WHOSE lists these are. Without this the panel kept a category from
  // the previous beneficiary and, worse, labelled it "Custom:" — on the
  // 529-eligible side, where the IRS list is read-only and a custom category is
  // not a thing that exists. (ZEN-282)
  const changeStudent = (nextId) => {
    const nextStu = students.find((s) => s.id === nextId);
    const nextGroup = (nextStu?.schools || []).filter((s) => !s.archived)[0]?.expense_group || "post_secondary";
    const nextFamily = nextStu?.noneligible_categories || [];
    setF((p) => ({
      ...p,
      student_id: nextId,
      category: reconcileCategory({ category: p.category, eligible: p.eligible_529, expenseGroup: nextGroup, familyCategories: nextFamily }),
    }));
  };

  // ZEN-162: the server's verdict on the reader's prefilled date (from the
  // receipt row), or a refused accept. The tick lifts it; editing the date
  // clears it for the server to re-judge — and so does CHOOSING A DIFFERENT
  // BENEFICIARY, because the window is theirs: the row-level flag was
  // computed before anyone was chosen, so it is stale the moment the choice
  // changes, in both directions. (Pre-merge diff review.)
  const dc = useDateConfirm(receipt.draft_date_flag);
  React.useEffect(() => { dc.resetFor(receipt.id, receipt.draft_date_flag); }, [receipt.id]);  // eslint-disable-line react-hooks/exhaustive-deps
  const _prevSid = React.useRef(f.student_id);
  React.useEffect(() => {
    if (_prevSid.current !== f.student_id) dc.clear();
    _prevSid.current = f.student_id;
  }, [f.student_id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const accept = async () => {
    if (!f.student_id) { toast.error("Select a beneficiary"); return; }
    if (!f.payor_type) { toast.error("Select who paid"); return; }
    if (!curCode(f.original_currency)) { toast.error("Select a currency"); return; }
    setBusy(true);
    try {
      if (receiptFilename && receiptFilename !== receipt.original_filename) {
        await api.patch(`/receipts/${receipt.id}`, { original_filename: receiptFilename });
      }
      const { _suggested_category, ...fields } = f;   // a suggestion is not a field
      const payload = {
        ...fields,
        original_amount: parseFloat(f.original_amount) || 0,
        paid_by: ["student", "owner", "529"].includes(f.payor_type) ? f.payor_type : "owner",
        payor_party_id: f.payor_type === "party" ? f.payor_party_id : null,
      };
      // An unanswered question is not sent as an answer: the keys are omitted
      // entirely and the expense lands under "Needs a decision". (ZEN-114)
      if (f.eligible_529 === null || f.eligible_529 === undefined) {
        delete payload.eligible_529; delete payload.draw_from_529; delete payload.category;
      } else if (f.eligible_529 === true && (f.draw_from_529 === null || f.draw_from_529 === undefined)) {
        toast.error(DRAW_QUESTION_UNANSWERED);
        setBusy(false); return;
      }
      await api.post(`/receipts/${receipt.id}/accept`, { expense: payload, date_confirmed: dc.confirmed });
      toast.success("Expense created from receipt");
      onDone();
    } catch (e) {
      if (!dc.applyRefusal(e)) toast.error(apiErr(e));
    }
    finally { setBusy(false); }
  };
  // ONE FLAG, SET AND READ BY ALL THREE — which is the rule, not three
  // separate habits. Accepting set it; rejecting set nothing and read nothing;
  // "Decide later" read it without ever setting it. So whichever answer was
  // travelling, at least one of the other two stayed live, and the panel's
  // three answers — which are ALTERNATIVES, not a queue — could be sent at
  // once. Swept before changing it: of the 15 screens in this app that hold a
  // busy flag and write, this panel was the asymmetric one.
  // "Remove", not "Reject": a receipt she uploaded herself is never called
  // rejected (Maryse, 13 October 2026, ZEN-752). The old address is used for
  // one release so the button works whichever server version answers.
  const remove = async () => {
    setBusy(true);
    try { await api.post(`/receipts/${receipt.id}/remove`); toast.info("Removed. You can upload the file again if you need it."); onDone(); } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  // DECIDE LATER DECIDES NOTHING, and that is the whole feature. (ZEN-634.)
  // Accept and Reject both end a receipt; this one records that somebody read
  // it and is not ready, which the app could not remember before — so leaving
  // a receipt alone looked exactly like never having opened it.
  //
  // It goes on to the next receipt in the queue rather than closing the panel,
  // because parking one is not finishing a session. Falling back to onDone
  // keeps the panel honest if it is ever rendered without somewhere to go.
  //
  // AND IT HOLDS THE PANEL WHILE IT IS IN FLIGHT, which the button already
  // CLAIMED with `disabled={busy}` and did not do — only accepting ever set
  // that flag. Nothing was harmed by the gap (the door is idempotent and a
  // second advance lands on the same receipt), but a control that advertises a
  // guard it does not have is worse than one that advertises nothing: the next
  // person to put something slower or less repeatable in here will read the
  // markup and reasonably believe double-pressing is already handled. Setting
  // it also holds Accept and Reject for the moment this request is out, which
  // is right — those three answers are alternatives, not a queue.
  const decideLater = async () => {
    setBusy(true);
    try {
      await api.post(`/receipts/${receipt.id}/set-aside`);
      (onSetAside || onDone)();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="mt-4 border-[#034AA2]" data-testid="review-panel">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 font-bold" data-testid="review-panel-heading"><Sparkle size={18} weight="fill" className="text-[#034AA2]" /> Review receipt — confirm proposed fields</div>
        <button onClick={onClose} aria-label="Close"><X size={20} /></button>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          {receipt.storage_path ? (
            <ReceiptViewer key={receipt.id} getPageSrc={(page) => receiptPreviewUrl(receipt.id, page)} pageCount={receipt.page_count || 1} alt={receiptFilename || "Receipt"} />
          ) : <div className="flex h-40 items-center justify-center rounded-lg border border-[#C2C9D1] bg-[#F8FAFC] p-8 text-sm text-[#6b7280]">No preview</div>}
          <div className="mt-2">
            <RRow label="Filename">
              <input value={receiptFilename} onChange={(e) => setReceiptFilename(e.target.value)} data-testid="review-filename" className="rinp" placeholder="e.g., 260515 Textbooks CAD179.81.pdf" />
            </RRow>
          </div>
        </div>
        <div className="space-y-3">
          {isStudentLocked ? (
            <div className="mb-1"><span className="text-xs font-bold uppercase tracking-wide text-[#6b7280]">Beneficiary</span> <span className="ml-2 text-sm font-semibold text-[#1f2937]" data-testid="review-student-locked">{selectedStudent?.full_name || "—"}</span></div>
          ) : (
            <div>
              <RRow label="Beneficiary"><select value={f.student_id} onChange={(e) => changeStudent(e.target.value)} data-testid="review-student" className="rinp"><option value="">Select…</option>{students.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}</select></RRow>
              {receiptAssignedName && f.student_id !== receipt.student_id && (
                <p className="mt-0.5 text-[10px] text-[#D97706] font-semibold" data-testid="review-student-notice">This receipt was uploaded for {receiptAssignedName}.</p>
              )}
              {!receipt.student_id && (
                <p className="mt-0.5 text-[10px] text-[#6b7280] font-semibold" data-testid="review-student-unassigned-notice">This receipt is not assigned to a beneficiary yet.</p>
              )}
            </div>
          )}
          <RRow label="Date" hint="from receipt"><div>
            <UnifiedDateInput value={f.transaction_date} onChange={(v) => { set("transaction_date", v); dc.clear(); }} testid="review-date" />
            <DateConfirmBanner message={dateFlagMessage(dc.flag, "owner")}
              confirmed={dc.confirmed} onConfirmedChange={dc.setConfirmed}
              testid="review-date-confirm" />
          </div></RRow>
          <RRow label="Payee" hint="from receipt"><input value={f.payee} onChange={(e) => set("payee", e.target.value)} data-testid="review-payee" className="rinp" /></RRow>
          <RRow label="Description"><input value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="review-desc" className="rinp" /></RRow>
          {/* ONE ACROSS ON A PHONE. Currency, Amount and Category were laid
              out three-across at every width — 396 pixels of fields on a
              375-pixel screen, so the panel scrolled sideways. Category has
              moved below the 529 question (ZEN-282) and the two that remain
              stack until there is room for both. (ZEN-169) */}
          <div className="grid grid-cols-1 items-end gap-2 sm:grid-cols-2">
            <RRow label={<>Currency <InfoTip testid="review-currency-info" text={CURRENCY_FIELD_HELP} /></>} hint="auto"><CurrencySelect value={f.original_currency} onChange={(v) => set("original_currency", v)} testid="review-currency" /></RRow>
            <RRow label="Amount" hint="from receipt"><MoneyInput currency={curCode(f.original_currency)} value={f.original_amount} onChange={(v) => set("original_amount", v)} testid="review-amount" placeholder="" /></RRow>
          </div>

          {/* 529 eligibility + draw routing */}
          <div className="rounded-lg border border-[#C2C9D1] bg-white p-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <RRow label="Is this expense 529-eligible?" hint={d.eligible_529 == null ? "needs your answer" : "suggested"}>
                <select value={f.eligible_529 === null || f.eligible_529 === undefined ? "" : (f.eligible_529 ? "yes" : "no")}
                  onChange={(e) => { if (e.target.value === "") return; answerEligibility(e.target.value === "yes"); }}
                  data-testid="review-eligible"
                  className={`rinp ${f.eligible_529 === null || f.eligible_529 === undefined ? "text-[#9CA3AF]" : ""}`}>
                  {(f.eligible_529 === null || f.eligible_529 === undefined) && <option value="" disabled>Choose an answer</option>}
                  <option value="yes">Yes — 529-eligible</option>
                  <option value="no">No — not 529-eligible</option>
                </select>
              </RRow>
              {/* CATEGORY SITS BELOW THE QUESTION THAT DECIDES ITS LIST, and
                  shows nothing until that question is answered. It used to sit
                  three fields above it, pre-filled. The 529-eligible list is
                  the IRS one for this beneficiary's education group; the
                  not-eligible list is the family's own, from Settings. Typing a
                  one-off name is offered only on the family's side — the IRS
                  list is not the family's to extend. (ZEN-282 / ZEN-114) */}
              <RRow label="Category">
                {choices === null ? (
                  <div className="flex h-10 w-full items-center rounded-md border border-dashed border-[#C2C9D1] bg-[#F8FAFC] px-3 text-xs text-[#6b7280]" data-testid="review-category-locked">{CATEGORY_LOCKED_HINT}</div>
                ) : (
                  <>
                    <select value={f.category || ""} onChange={(e) => {
                      const v = e.target.value;
                      if (v === ADD_CATEGORY_OPTION) { setCustomCatOpen(false); setAddCatOpen(true); return; }
                      if (v === OTHER_MANUAL) { setAddCatOpen(false); setCustomCatOpen(true); return; }
                      set("category", v);
                    }} data-testid="review-category" className={`rinp ${f.category ? "" : "text-[#9CA3AF]"}`}>
                      {!f.category && <option value="" disabled>Select an expense category</option>}
                      {choices.map((c) => <option key={c} value={c} className="text-[#1f2937]">{c}</option>)}
                      {/* MOVED, NOT CHANGED. This screen invented the rule; it now
                          reads it from the shared file so the expense box can use
                          the same one rather than a second copy. (ZEN-685) */}
                      {isOffListCategory(f.category, choices) && <option value={f.category} className="text-[#1f2937]">{f.category}</option>}
                      {f.eligible_529 === false && <option value={ADD_CATEGORY_OPTION}>{ADD_CATEGORY_LABEL}</option>}
                      {f.eligible_529 === false && <option value={OTHER_MANUAL}>{OTHER_MANUAL}</option>}
                    </select>
                    {/* THE BOX MAY NOT OUTLIVE THE ANSWER THAT JUSTIFIED IT.
                        (ZEN-100) Typing a one-off name belongs to the NON-529
                        list only. The OPTION was gated on "not eligible" but the
                        box it opens was not, so an owner could open it, switch
                        the 529 answer to Yes, confirm, and store free text as
                        the category of a QUALIFIED expense — the eligible list
                        is the IRS reference and is not the family's to extend.
                        Gated on render AND on confirm: the answer can change
                        while the box is open, which is the whole defect. */}
                    {f.eligible_529 === false && customCatOpen && (
                      <div className="mt-1.5 flex items-center gap-1.5">
                        <input value={customCatName} onChange={(e) => setCustomCatName(e.target.value.slice(0, 22))} placeholder="Custom category name" data-testid="review-custom-cat-input" className="rinp flex-1" autoFocus />
                        <button type="button" onClick={() => { if (!isReservedCategoryName(customCatName, REVIEW_RESERVED_NAMES) && customCatName.trim() && f.eligible_529 === false) set("category", customCatName.trim()); setCustomCatOpen(false); setCustomCatName(""); }} data-testid="review-custom-cat-confirm"
                          className="h-10 rounded-md bg-[#034AA2] px-3 text-xs font-semibold text-white hover:bg-[#023d8a] disabled:opacity-50" disabled={!customCatName.trim() || isReservedCategoryName(customCatName, REVIEW_RESERVED_NAMES)}>OK</button>
                        <button type="button" onClick={() => { setCustomCatOpen(false); setCustomCatName(""); }} className="h-10 rounded-md border border-[#C2C9D1] px-3 text-xs font-semibold text-[#6b7280] hover:bg-[#E9E9EB]">Cancel</button>
                      </div>
                    )}
                    {/* THE SHARED BOX, for the beneficiary chosen now. A save that
                        answers after the beneficiary or the 529 answer changed
                        updates the list and chooses nothing. (ZEN-512) */}
                    {f.eligible_529 === false && addCatOpen && activeStu && (
                      <AddCategoryInline
                        key={activeStu.id}
                        student={activeStu}
                        testid="review-cat-add"
                        isStillWanted={(savedFor) => formNow.current.student_id === savedFor && formNow.current.eligible_529 === false}
                        onCancel={() => setAddCatOpen(false)}
                        onChosen={(stored) => { set("category", stored); setAddCatOpen(false); }} />
                    )}
                    {f.eligible_529 === false && familyCategories.length === 0 && !customCatOpen && !addCatOpen && (
                      <div className="mt-0.5 text-[11px] font-semibold text-[#B45309]" data-testid="review-no-family-cats">{NO_FAMILY_CATEGORIES_HINT}</div>
                    )}
                    {isOffListCategory(f.category, choices) && !customCatOpen && (
                      <div className="mt-0.5 text-[10px] font-semibold text-[#034AA2]">{offListCategoryHint(f.category)}</div>
                    )}
                  </>
                )}
              </RRow>
              {f.eligible_529 === true && (
                <RRow label={DRAW_QUESTION}>
                  <select value={f.draw_from_529 === null || f.draw_from_529 === undefined ? "" : (f.draw_from_529 ? "yes" : "no")}
                    onChange={(e) => { if (e.target.value === "") return; set("draw_from_529", e.target.value === "yes"); }}
                    data-testid="review-draw"
                    className={`rinp ${f.draw_from_529 === null || f.draw_from_529 === undefined ? "text-[#9CA3AF]" : ""}`}>
                    {(f.draw_from_529 === null || f.draw_from_529 === undefined) && <option value="" disabled>Choose an answer</option>}
                    <option value="yes">{DRAW_YES_LABEL}</option>
                    <option value="no">{DRAW_NO_LABEL}</option>
                  </select>
                </RRow>
              )}
              {(f.eligible_529 === false || f.draw_from_529 === false) && (
                <div>
                  <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-[#6b7280]">Reimbursed by a party?</span>
                  <select value={f.reimbursed_by_ref} onChange={(e) => { if (e.target.value === "__add_reimburser__") { setAddReimburserOpen(true); } else { set("reimbursed_by_ref", e.target.value); } }} data-testid="review-reimbursed" className="rinp">
                    <option value="none">Not reimbursed</option>
                    <option value="owner">{parties?.owner?.nickname || "Owner"}</option>
                    {(parties?.custom || []).map((p) => <option key={p.id} value={`party:${p.id}`}>{p.nickname}</option>)}
                    <option value="__add_reimburser__">＋ Add party…</option>
                  </select>
                  {addReimburserOpen && (
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <input value={newReimburserNick} onChange={(e) => setNewReimburserNick(e.target.value)} placeholder="Nickname (e.g., Grandpa)" autoFocus data-testid="review-add-reimburser-input"
                        className="rinp flex-1" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); document.querySelector('[data-testid="review-add-reimburser-ok"]')?.click(); } }} />
                      <button type="button" data-testid="review-add-reimburser-ok" disabled={!newReimburserNick.trim()}
                        className="h-10 rounded-md bg-[#034AA2] px-3 text-xs font-semibold text-white hover:bg-[#023d8a] disabled:opacity-50"
                        onClick={async () => { try { const { data: np } = await api.post("/parties", { full_name: newReimburserNick.trim(), nickname: newReimburserNick.trim(), role: "other" }); qc.invalidateQueries({ queryKey: ["parties"] }); set("reimbursed_by_ref", `party:${np.id}`); } catch (e) { toast.error(apiErr(e)); } setAddReimburserOpen(false); setNewReimburserNick(""); }}>
                        Add</button>
                      <button type="button" onClick={() => { setAddReimburserOpen(false); setNewReimburserNick(""); set("reimbursed_by_ref", "none"); }} className="h-10 rounded-md border border-[#C2C9D1] px-3 text-xs font-semibold text-[#6b7280] hover:bg-[#E9E9EB]">Cancel</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* The tax year is no longer a box here — it follows the date paid, on
              the server. Stated so the reviewer can see it and check it against
              the receipt, never typed. (ZEN-172) */}
          <div className="grid grid-cols-2 gap-2">
            <RRow label="Counts toward tax year">
              <div data-testid="review-taxyear" className="flex h-9 items-center text-sm text-[#374151]">
                {(f.transaction_date || "").slice(0, 4) || "—"}
                <span className="ml-2 text-[11px] text-[#9aa1a9]">set by the date paid</span>
              </div>
            </RRow>
          </div>

          {/* Paid by */}
          <div data-testid="review-paidby-group">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-[#6b7280]">Paid by</span>
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { key: "student", label: "Student", sub: activeStu?.nickname || activeStu?.full_name?.split(" ")[0] || "Beneficiary" },
                { key: "owner", label: "Owner", sub: parties?.owner?.nickname || "Owner" },
                { key: "529", label: "529", sub: "Direct" },
              ].map((opt) => (
                <button key={opt.key} type="button" onClick={() => { set("payor_type", opt.key); set("payor_party_id", ""); }} data-testid={`review-paidby-${opt.key}`}
                  className={`flex h-9 items-center gap-1 rounded-md px-3 text-sm font-semibold transition-colors ${f.payor_type === opt.key ? "bg-[#034AA2] text-white" : "border border-[#C2C9D1] bg-white hover:bg-[#E9E9EB]"}`}>
                  {opt.label}<span className={`text-xs font-normal ${f.payor_type === opt.key ? "text-blue-100" : "text-[#6b7280]"}`}>· {opt.sub}</span>
                </button>
              ))}
              {(parties?.custom || []).map((p) => (
                <button key={p.id} type="button" onClick={() => { set("payor_type", "party"); set("payor_party_id", p.id); }} data-testid={`review-paidby-party-${p.id}`}
                  className={`h-9 rounded-md px-3 text-sm font-semibold transition-colors ${f.payor_type === "party" && f.payor_party_id === p.id ? "bg-[#034AA2] text-white" : "border border-[#C2C9D1] bg-white hover:bg-[#E9E9EB]"}`}>
                  {p.nickname}
                </button>
              ))}
              <a href="/settings?tab=parties" target="_blank" rel="noreferrer" data-testid="review-paidby-add"
                className="flex h-9 items-center gap-1 rounded-md border border-dashed border-[#034AA2] px-3 text-sm font-semibold text-[#034AA2] transition-colors hover:bg-[#034AA2]/5">
                <Plus size={14} weight="bold" /> Add payer
              </a>
            </div>
          </div>

          {/* Payment account */}
          <RRow label="Payment account">
            <p className="mb-1 text-[10px] text-[#6b7280] font-normal normal-case">Informational only — doesn't affect reimbursement.</p>
            {f.payor_type === "529" ? (
              <PaidByPlanLine testid="review-payment-529" studentId={f.student_id}
                ownerName={parties?.owner?.full_name || parties?.owner?.nickname || ""}
                beneficiaryName={activeStu?.full_name || ""}
                beneficiaryNick={activeStu?.nickname || activeStu?.full_name?.split(" ")[0] || "Beneficiary"} />
            ) : (
              <select value={f.payment_source_id || ""} onChange={(e) => set("payment_source_id", e.target.value || null)} data-testid="review-payment-source" className="rinp">
                <option value="">— None —</option>
                {payerAccounts.map((s) => <option key={s.id} value={s.id}>{psLabel(s, psOwnerName(s, students, parties))}</option>)}
              </select>
            )}
          </RRow>

          {/* Notes */}
          <RRow label="Notes">
            <input value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="review-notes" className="rinp" placeholder="Optional notes…" />
          </RRow>

          {dupWarnings.length > 0 && (
            <div className="rounded-md border border-[#D97706] bg-[#FEF3C7] px-3 py-2 text-xs" data-testid="dup-warning-banner">
              <b className="text-[#D97706]">Possible duplicate found:</b>
              {dupWarnings.map((dup) => (
                <div key={dup.id} className="mt-1.5 flex items-center justify-between gap-2 rounded-md bg-white/60 px-2 py-1.5">
                  <span className="text-[#1f2937]"><b>{dup.payee}</b> — {fmtDate(dup.date)} — <span className="capitalize">{dup.status}</span>{dup.description ? ` — ${dup.description}` : ""}</span>
                  <button type="button" data-testid={`attach-to-expense-${dup.id}`}
                    onClick={async () => {
                      try {
                        await api.post(`/receipts/${receipt.id}/attach/${dup.id}`);
                        toast.success("Receipt attached to existing expense");
                        onDone();
                      } catch (e) { toast.error(apiErr(e)); }
                    }}
                    className="shrink-0 rounded-md bg-[#034AA2] px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-[#023d8a]">
                    Attach to this expense
                  </button>
                </div>
              ))}
              <div className="mt-1 text-[11px] text-[#7D3306]">Or continue below to create a new expense if this is a separate transaction.</div>
            </div>
          )}
          {/* THE TWO BUTTONS STACK BEFORE ONE OF THEM GETS CUT OFF. Side by
              side they need 20 more pixels than the panel has on a 375-pixel
              phone, and "Reject" was the half that fell off the edge — a
              control the account owner could see part of and not reach.
              Found while measuring this panel for ZEN-169, on the same screen
              and at the same width. */}
          <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:items-center">
            <Btn variant="green" onClick={accept} disabled={busy} data-testid="accept-receipt-btn"><Check size={16} weight="bold" /> Accept & create expense</Btn>
            {/* DECIDE LATER SITS BETWEEN THE TWO DECISIONS, in the quiet
                variant, because it is the one button here that settles
                nothing. Wording approved by Maryse with the ticket: not
                "Skip", which reads as discard, and the point of this ruling is
                that nothing is discarded without being read. It stacks with
                the other two on a narrow screen for the reason written above
                them — side by side they do not fit a 375-pixel phone. */}
            <Btn variant="ghost" onClick={decideLater} disabled={busy} data-testid="decide-later-btn"><Clock size={16} weight="bold" /> Decide later</Btn>
            {/* HELD WHILE EITHER OF THE OTHER TWO IS IN FLIGHT, which it was
                not. Reject had no guard at all, so it stayed live during a
                set-aside request — and the two race: rejecting accepts a
                set-aside receipt, so whichever lands second decides. Neither
                order corrupts anything (the set-aside door refuses a decided
                receipt outright) but the panel's three answers are
                ALTERNATIVES, not a queue, and this change's own comment
                claimed they were already held. One attribute wider than
                "Decide later" strictly needed, done so the claim is true
                rather than trimmed to fit. (Adversarial review, round 1.) */}
            <Btn variant="danger" onClick={remove} disabled={busy} data-testid="remove-receipt-btn"><X size={16} weight="bold" /> Remove</Btn>
          </div>
        </div>
      </div>
      <style>{`.rinp{height:40px;width:100%;border:1px solid #C2C9D1;border-radius:6px;padding:0 10px;font-size:14px;background:#fff}`}</style>
    </Card>
  );
}


// THE BADGE SAYS WHAT HAPPENED, NOT THAT THE APP DECIDED. (ZEN-513, owner
// ruling 2026-09-06.) It used to read "auto" on the date, the payee, the amount
// and the currency. Her words: AUTO implies the app decided something; FROM
// RECEIPT says what actually happened — this is what we read on the paper, check
// it — which invites the check instead of implying it is handled. On a screen
// where every value becomes a tax record, a badge that sounds settled is a badge
// that stops people looking.
//
// CURRENCY IS NOT RENAMED HERE. She ruled separately that currency must never
// say AUTO: it shows the most likely three-letter code based on where the
// student goes to school, confirmed by the reviewer. That is ZEN-514, and "from
// receipt" would be the wrong words for it — the code comes from the school, not
// off the paper. Its badge is left for that ticket to settle.
function RRow({ label, hint, children }) {
  return (
    <label className="block">
      {/* THE BADGE KEEPS ITS WORDS TOGETHER AND DROPS TO ITS OWN LINE. "from
          receipt" is three times the length of the "auto" it replaced, and in a
          narrow column it broke across two lines mid-phrase — "FROM / RECEIPT"
          — which also pushed the Amount box below the Currency box beside it,
          so the two stopped lining up. Looked at rather than assumed: it showed
          at 320px and 260px, widths this panel really reaches on a tablet. */}
      <span className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs font-bold uppercase tracking-wide text-[#6b7280]">{label}{hint && <em className="whitespace-nowrap rounded bg-[#034AA2]/10 px-1.5 py-0.5 not-italic text-[10px] font-semibold text-[#034AA2]">{hint}</em>}</span>
      {children}
    </label>
  );
}



export { ReceiptQueue, ReceiptReviewPanel };
